import { useState, useEffect } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { MapPin, Package, Users, Clock } from "lucide-react";

export default function QuotationComparison() {
  const [pos, setPos] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);

  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [previewQuote, setPreviewQuote] = useState(null);

  // ✅ FETCH POs
  useEffect(() => {
    fetch("/api/admin/purchase-orders", {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setPos(data.data);
        }
      });
  }, []);

  useEffect(() => {
    if (!selectedPO) return;

    loadQuotations(selectedPO.id);
  }, [selectedPO]);

  const loadQuotations = async (poId) => {
    const res = await fetch(`/api/admin/quotations/${poId}`, {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      setQuotes(data.data);
      console.log(data.data)

      const approved = data.data.find((q) => q.status === "Approved");

      if (approved) {
        setSelectedQuote(approved);
      } else {
        setSelectedQuote(null);
      }
    }
  };

  // ✅ TOTALS (same logic)
  const totals = quotes.map((q) => Number(q.price || 0));

  const cheapestIndex = totals.indexOf(Math.min(...totals));

  const fastestIndex = quotes
    .map((q) => Number(q.deliveryDays || Infinity))
    .indexOf(
      Math.min(...quotes.map((q) => Number(q.deliveryDays || Infinity))),
    );

  // ✅ FINALIZE
  const finalizeOrder = async () => {
    if (!selectedQuote) {
      alert("Please select a vendor first.");
      return;
    }

    if (
      !window.confirm(
        `Finalize ${selectedQuote.vendor_name}? This cannot be undone.`,
      )
    ) {
      return;
    }

    const res = await fetch("/api/admin/finalize-quotation", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        po_id: selectedPO.id,
        vendor_id: selectedQuote.vendor_id,
      }),
    });

    const data = await res.json();

    if (data.status === "success") {
      await loadQuotations(selectedPO.id);

      alert("Vendor finalized successfully.");
    } else {
      alert(data.message);
    }
  };

  const isPOFinalized = quotes.some((q) => q.status === "Approved");

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* ================== PO LIST ================== */}
        {!selectedPO && (
          <>
            <h1 className="text-2xl font-serif">Purchase Orders</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pos.map((po) => (
                <div
                  key={po.id}
                  onClick={() => setSelectedPO(po)}
                  className="cursor-pointer rounded-xl bg-white/5 border border-white/10 p-6 hover:border-amber-400 transition"
                >
                  {/* TOP */}
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-lg">{po.po_number}</p>

                    <span
                      className={`px-3 py-1 rounded-full text-xs ${
                        po.status === "Approved"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : po.status === "Pending Approval"
                            ? "bg-blue-500/20 text-blue-400"
                            : "bg-amber-400/20 text-amber-400"
                      }`}
                    >
                      {po.status}
                    </span>
                  </div>

                  {/* BRANCH */}
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
                    <MapPin size={14} />
                    <span>{po.location || "—"}</span>
                  </div>

                  {/* DETAILS */}
                  <div className="mt-4 text-sm text-gray-300 space-y-2">
                    <div className="flex items-center gap-2">
                      <Package size={14} />
                      <span>
                        Delivery:{" "}
                        {po.delivery_date
                          ? new Date(po.delivery_date).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Users size={14} />
                      <span>Vendors: {po.vendors || 0}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Clock size={14} />
                      <span>
                        Created:{" "}
                        {po.created_at
                          ? new Date(po.created_at).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ================== QUOTATION TABLE ================== */}
        {selectedPO && (
          <>
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-serif">
                Quotations – {selectedPO.po_number}
              </h1>

              <button
                onClick={() => {
                  setSelectedPO(null);
                  setQuotes([]);
                  setSelectedQuote(null);
                }}
                className="px-4 py-2 border border-white/20 rounded-lg"
              >
                Back
              </button>
            </div>

            <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-black/40 text-gray-400">
                  <tr>
                    <th className="px-6 py-4 text-left">Vendor</th>
                    <th className="px-6 py-4">Price</th>
                    <th className="px-6 py-4">Delivery Days</th>
                    <th className="px-6 py-4">Total</th>
                    <th className="px-6 py-4">Tags</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {quotes.map((q, i) => {
                    const total = totals[i];
                    const isSelected = selectedQuote?.id === q.id;
                    const isFinalized = q.status === "Approved";

                    return (
                      <tr
                        key={q.id}
                        className={`border-t border-white/10 ${
                          isSelected ? "bg-amber-400/10" : "hover:bg-white/5"
                        }`}
                      >
                        <td className="px-6 py-4 font-medium">
                          {q.vendor_name}

                          {q.status && (
                            <span
                              className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${
                                q.status === "Approved"
                                  ? "bg-emerald-500/20 text-emerald-400"
                                  : q.status === "Rejected"
                                    ? "bg-red-500/20 text-red-400"
                                    : "bg-yellow-500/20 text-yellow-400"
                              }`}
                            >
                              {q.status}
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center">₹{q.price}</td>

                        <td className="px-6 py-4 text-center">
                          {q.deliveryDays}
                        </td>

                        <td className="px-6 py-4 text-center">₹{total}</td>

                        <td className="px-6 py-4 text-center space-x-2">
                          {i === cheapestIndex && (
                            <span className="px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
                              Cheapest
                            </span>
                          )}
                          {i === fastestIndex && (
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                              Fastest
                            </span>
                          )}
                          {q.status === "Approved" && (
                            <span className="px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400">
                              ✔ Approved
                            </span>
                          )}

                          {q.status === "Rejected" && (
                            <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
                              ✖ Rejected
                            </span>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center space-x-2">
                          <button
                            onClick={() => setPreviewQuote(q)}
                            className="px-3 py-1 rounded-lg border border-white/20 hover:bg-white/10"
                          >
                            View
                          </button>

                          <button
                            disabled={isPOFinalized}
                            onClick={() => setSelectedQuote(q)}
                            className={`px-3 py-1 rounded-lg transition ${
                              isSelected
                                ? "bg-amber-400 text-black"
                                : isPOFinalized
                                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                                  : "border border-white/20 hover:bg-white/10"
                            }`}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <button
                disabled={isPOFinalized}
                onClick={finalizeOrder}
                className={`px-6 py-3 rounded-xl font-medium transition ${
                  isPOFinalized
                    ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                    : "bg-amber-400 text-black hover:bg-amber-300"
                }`}
              >
                {isPOFinalized
                  ? "Already Finalized"
                  : "Finalize Selected Vendor"}
              </button>
            </div>
          </>
        )}

        {/* ================== MODAL ================== */}
        {previewQuote && (
          <Modal onClose={() => setPreviewQuote(null)}>
            <h2 className="text-xl font-serif mb-4">
              Quotation – {previewQuote.vendor_name}
            </h2>

            <div className="space-y-2 text-sm">
              <p>Price: ₹{previewQuote.price}</p>
              <p>Delivery: {previewQuote.deliveryDays} days</p>
              <p>Total: ₹{previewQuote.price}</p>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setPreviewQuote(null)}
                className="px-5 py-2 rounded-xl bg-amber-400 text-black"
              >
                Close
              </button>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
      <div className="w-full max-w-xl rounded-2xl bg-[#0b0b0f] border border-white/10 p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
}
